import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlayingPlatformSettings, savePlayingPlatformSettings } from '../../api/integrations';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { CheckboxField } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage } from '../../utils/apiError';

export default function PlayingPlatformSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const s = await getPlayingPlatformSettings();
      setSettings({ spectatorsAllowed: s?.spectatorsAllowed !== false });
    } catch (e) {
      setError(apiErrorMessage(e, t('settings.playingPlatform.loadFailed', {
        defaultValue: 'Could not load playing platform settings.',
      })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirtyValue = settings;
  const persist = async () => {
    const saved = await savePlayingPlatformSettings(settings);
    const next = { spectatorsAllowed: saved?.spectatorsAllowed !== false };
    setSettings(next);
    return next;
  };
  const { markSaved } = useUnsavedChanges(dirtyValue, {
    enabled: !!settings,
    onSave: async () => {
      await persist();
    },
  });

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const next = await persist();
      markSaved(next);
      setSuccess(t('settings.playingPlatform.saveSuccess', {
        defaultValue: 'Playing platform settings saved.',
      }));
    } catch (err) {
      setError(apiErrorMessage(err, t('settings.playingPlatform.saveFailed', {
        defaultValue: 'Could not save settings.',
      })));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <PageLoadingShell
        title={t('settings.playingPlatform.title', { defaultValue: 'Playing platform' })}
        subtitle={t('settings.playingPlatform.subtitle', {
          defaultValue: 'Academy-wide rules for live play and challenges.',
        })}
      />
    );
  }

  return (
    <div className="page-shell-form max-w-2xl">
      <PageHeaderSection
        backTo="/admin"
        title={t('settings.playingPlatform.title', { defaultValue: 'Playing platform' })}
        subtitle={t('settings.playingPlatform.subtitle', {
          defaultValue: 'Academy-wide rules for live play and challenges.',
        })}
      />

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {success}
        </div>
      )}

      <form onSubmit={save} className="section-card space-y-5 p-5">
        <CheckboxField
          layout="toggle-card"
          label={t('settings.playingPlatform.spectatorsAllowed', {
            defaultValue: 'Allow spectators on human games',
          })}
          description={t('settings.playingPlatform.spectatorsNote', {
            defaultValue: 'When on, live games and challenges can be watched from Live games. When off, games stay private to the two players (and staff). Players no longer choose this per game.',
          })}
          checked={!!settings.spectatorsAllowed}
          onChange={(e) => setSettings((prev) => ({ ...prev, spectatorsAllowed: e.target.checked }))}
        />

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button type="submit" variant="page-primary" loading={saving} disabled={saving}>
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </div>
      </form>
    </div>
  );
}
